import { Table as TanstackTable } from '@tanstack/react-table'
import { ComponentProps, useCallback, useMemo } from 'react'

import { Checkbox, LinkButton, Table } from '../..'
import { InvoiceDetailsResponse } from '../../../hooks/useApi/types'
import { useTranslation } from 'react-i18next'
import { useBillingApi } from '../../../hooks/useApi/useBillingApi/useBillingApi'
import useToastStore from '../../../stores/useToastStore/useToastStore'

type SelectedTableHeaderProps = {
    table: TanstackTable<unknown>
} & Pick<ComponentProps<typeof Table>, 'style'>

export const SelectedTableHeader: React.FC<SelectedTableHeaderProps> = ({
    table,
}) => {
    const { t } = useTranslation('translations')

    const selectedDocumentsIds = table
        .getFilteredSelectedRowModel()
        .flatRows.map((row) => row.original)
        .map((invoice) => (invoice as InvoiceDetailsResponse).invoiceDocId)
        .filter((id): id is number => id !== null && id !== undefined)

    const { useGetDocuments, useGetDocument } = useBillingApi()
    const addAlert = useToastStore((state) => state.addAlert)

    const handleError = () => {
        addAlert({
            variant: 'error',
            title: t(
                'billing_and_payments.payables_summary.download_documents.error_toast_title'
            ),
            description: t(
                'billing_and_payments.payables_summary.download_documents.error_toast_description'
            ),
        })
    }

    const { refetch: refetchSingle } = useGetDocument(selectedDocumentsIds[0], {
        onError: handleError,
    })

    const { refetch: refetchMultiple } = useGetDocuments(selectedDocumentsIds, {
        onError: handleError,
    })

    const refetch = useMemo(() => {
        if (selectedDocumentsIds.length === 1) {
            return refetchSingle
        }

        return async () => {
            addAlert({
                variant: 'success',
                title: t(
                    'billing_and_payments.payables_summary.download_documents.start_toast_title'
                ),
                description: t(
                    'billing_and_payments.payables_summary.download_documents.start_toast_description'
                ),
            })

            await refetchMultiple()
        }
    }, [
        selectedDocumentsIds.length,
        refetchSingle,
        refetchMultiple,
        addAlert,
        t,
    ])

    const onDownloadClick = useCallback(() => {
        void refetch()
    }, [refetch])

    return (
        <thead className="sticky top-0 z-30 h-12 border-r border-neutral-200 bg-neutral-100 px-4 py-3 font-inter text-sm font-normal leading-6">
            <tr>
                <th colSpan={1}>
                    <div className="flex w-full items-center justify-center">
                        <Checkbox
                            name="select-all-rows-checkbox"
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                        />
                    </div>
                </th>

                <th
                    colSpan={table.getAllColumns().length - 1}
                    className="w-full px-4"
                >
                    <div className="flex items-center justify-start gap-6">
                        <SelectedCount
                            count={
                                table.getFilteredSelectedRowModel().flatRows
                                    .length
                            }
                            t={t}
                        />

                        <LinkButton
                            name="download"
                            label={t('common.download')}
                            iconBefore="download"
                            onClick={onDownloadClick}
                        />
                    </div>
                </th>
            </tr>
        </thead>
    )
}

const SelectedCount: React.FC<{
    count: number
    t: (key: string, options?: Record<string, unknown>) => string
}> = ({ count, t }) => {
    const text = t('payables_summary_table.selected', {
        count,
        interpolation: { escapeValue: false },
    })

    return <span dangerouslySetInnerHTML={{ __html: text }} />
}
